type PreviewProfile = {
  display_name?: string | null;
};

type ChannelPreviewPost = {
  channel_id?: string | null;
  content?: string | null;
  created_at?: string | null;
  profiles?: PreviewProfile | PreviewProfile[] | null;
};

type ChannelPreviewReply = {
  channel_id?: string | null;
  content?: string | null;
  created_at?: string | null;
  post?: {
    channel_id?: string | null;
  } | null;
  posts?:
    | {
        channel_id?: string | null;
      }
    | Array<{
        channel_id?: string | null;
      }>
    | null;
  profiles?: PreviewProfile | PreviewProfile[] | null;
};

type ChatRoomPreviewMessage = {
  room_id?: string | null;
  content?: string | null;
  created_at?: string | null;
  is_deleted?: boolean | null;
  profiles?: PreviewProfile | PreviewProfile[] | null;
};

export type CommunityActivityPreview = {
  actorName: string | null;
  content: string | null;
  createdAt: string | null;
};

const toTimestamp = (value?: string | null) => {
  if (!value) {
    return 0;
  }

  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeProfile = (profile: PreviewProfile | PreviewProfile[] | null | undefined) =>
  Array.isArray(profile) ? profile[0] ?? null : profile ?? null;

const getReplyChannelId = <TReply extends ChannelPreviewReply>(reply: TReply) => {
  if (reply.channel_id) {
    return reply.channel_id;
  }

  if (Array.isArray(reply.posts)) {
    return reply.posts[0]?.channel_id ?? null;
  }

  return reply.post?.channel_id ?? reply.posts?.channel_id ?? null;
};

const toPreview = (
  content?: string | null,
  profile?: PreviewProfile | PreviewProfile[] | null,
  createdAt?: string | null,
): CommunityActivityPreview => ({
  actorName: normalizeProfile(profile)?.display_name ?? null,
  content: content?.trim() || null,
  createdAt: createdAt ?? null,
});

export const buildChannelActivityPreviewMap = <
  TPost extends ChannelPreviewPost,
  TReply extends ChannelPreviewReply,
>(
  posts: TPost[],
  replies: TReply[],
) => {
  const previewByChannelId: Record<string, CommunityActivityPreview> = {};

  posts.forEach((post) => {
    if (!post.channel_id || !post.created_at) {
      return;
    }

    const current = previewByChannelId[post.channel_id];
    if (toTimestamp(current?.createdAt) >= toTimestamp(post.created_at)) {
      return;
    }

    previewByChannelId[post.channel_id] = toPreview(post.content, post.profiles, post.created_at);
  });

  replies.forEach((reply) => {
    const channelId = getReplyChannelId(reply);
    if (!channelId || !reply.created_at) {
      return;
    }

    const current = previewByChannelId[channelId];
    if (toTimestamp(current?.createdAt) >= toTimestamp(reply.created_at)) {
      return;
    }

    previewByChannelId[channelId] = toPreview(reply.content, reply.profiles, reply.created_at);
  });

  return previewByChannelId;
};

export const buildRoomActivityPreviewMap = <TMessage extends ChatRoomPreviewMessage>(
  messages: TMessage[],
) => {
  const previewByRoomId: Record<string, CommunityActivityPreview> = {};

  messages.forEach((message) => {
    if (!message.room_id || !message.created_at || message.is_deleted) {
      return;
    }

    const current = previewByRoomId[message.room_id];
    if (toTimestamp(current?.createdAt) >= toTimestamp(message.created_at)) {
      return;
    }

    previewByRoomId[message.room_id] = toPreview(message.content, message.profiles, message.created_at);
  });

  return previewByRoomId;
};
